const state = {
    /**
     * An array of all available filters definitions
     */
    availableFilters: [],

    /**
     * An array of active filter IDs in the order they were added
     */
    activeFilters: [],

    /**
     * The currently open filter accordion (ID or null)
     */
    openFilter: null,

    /**
     * Object containing filter values (key-value pairs)
     */
    filtersData: {}
};

const getters = {
    /**
     * Get all available filters
     */
    availableFilters: state => state.availableFilters,

    /**
     * Get the IDs of active filters
     */
    activeFilters: state => state.activeFilters,

    /**
     * Get active filter objects in the correct order
     */
    activeFilterSections: state => {
        return state.activeFilters
            .map(id => state.availableFilters.find(f => f.id === id))
            .filter(Boolean);
    },

    /**
     * Get the currently open filter ID
     */
    openFilter: state => state.openFilter,

    /**
     * Get all filter data
     */
    filtersData: state => state.filtersData
};

const mutations = {
    /**
     * Set the initial available filters
     */
    SET_AVAILABLE_FILTERS(state, filters) {
        state.availableFilters = filters;
    },


    /**
     * Set active filters array
     */
    SET_ACTIVE_FILTERS(state, filterIds) {
        state.activeFilters = filterIds;
    },

    /**
     * Add a filter to the active list
     */
    ADD_ACTIVE_FILTER(state, filterId) {
        if (!state.activeFilters.includes(filterId)) {
            state.activeFilters.push(filterId);
        }
    },

    /**
     * Remove a filter from the active list
     */
    REMOVE_ACTIVE_FILTER(state, filterId) {
        state.activeFilters = state.activeFilters.filter(id => id !== filterId);
    },

    /**
     * Set the currently open filter
     */
    SET_OPEN_FILTER(state, filterId) {
        state.openFilter = filterId;
    },

    /**
     * Add an option to a filter's options list
     */
    ADD_OPTION_TO_FILTER(state, {filterId, option}) {
        const filter = state.availableFilters.find(f => f.id === filterId);
        if (filter && !filter.options.some(o => o.value === option.value)) {
            filter.options.push(option);
        }
    },

    /**
     * Update a specific filter value
     */
    UPDATE_FILTER_VALUE(state, {key, value}) {
        state.filtersData = {
            ...state.filtersData,
            [key]: value
        };
    },

    /**
     * Remove a filter's data
     */
    REMOVE_FILTER_DATA(state, {includeKey, excludeKey}) {
        const newData = {...state.filtersData};
        if (newData[includeKey] !== undefined) {
            delete newData[includeKey];
        }
        if (excludeKey && newData[excludeKey] !== undefined) {
            delete newData[excludeKey];
        }
        state.filtersData = newData;
    },

    /**
     * Initialize filter data
     */
    INITIALIZE_FILTER_DATA(state, {includeKey, excludeKey}) {
        const newData = {...state.filtersData};
        if (newData[includeKey] === undefined) {
            newData[includeKey] = [];
        }
        if (excludeKey && newData[excludeKey] === undefined) {
            newData[excludeKey] = [];
        }
        state.filtersData = newData;
    },

    /**
     * Set all filter data at once
     */
    SET_FILTERS_DATA(state, data) {
        state.filtersData = data;
    }
};

const actions = {
    /**
     * Initialize the filters store with available filters
     */
    initializeFilters({commit}, filters) {
        commit('SET_AVAILABLE_FILTERS', filters);
    },

    /**
     * Synchronize active filters based on filter data
     */
    syncActiveFilters({commit, state}) {
        // Find all filters that have data
        const filtersWithData = {};

        state.availableFilters.forEach(filter => {
            const includeKey = filter.include_key;
            const excludeKey = filter.exclude_key;

            if (
                (state.filtersData[includeKey] && state.filtersData[includeKey].length > 0) ||
                (excludeKey && state.filtersData[excludeKey] && state.filtersData[excludeKey].length > 0)
            ) {
                filtersWithData[filter.id] = true;
            }
        });

        // Keep filters in original order if they have data
        const newActiveFilters = state.activeFilters.filter(id => filtersWithData[id]);

        // Add any filters with data that aren't already in the list
        state.availableFilters.forEach(filter => {
            if (filtersWithData[filter.id] && !newActiveFilters.includes(filter.id)) {
                newActiveFilters.push(filter.id);
            }
        });

        // Add any filters without data that were previously active
        state.activeFilters.forEach(filterId => {
            if (!newActiveFilters.includes(filterId)) {
                newActiveFilters.push(filterId);
            }
        });

        // Update only if different
        if (JSON.stringify(newActiveFilters) !== JSON.stringify(state.activeFilters)) {
            commit('SET_ACTIVE_FILTERS', newActiveFilters);
        }
    },

    resetFilters({commit}) {
        commit('SET_AVAILABLE_FILTERS', []);
        commit('SET_ACTIVE_FILTERS', []);
        commit('SET_OPEN_FILTER', null);
        commit('SET_FILTERS_DATA', {});
    },

    /**
     * Add a filter to the active filters list and initialize its data
     */
    addFilter({commit}, filter) {
        commit('ADD_ACTIVE_FILTER', filter.id);
        commit('INITIALIZE_FILTER_DATA', {
            includeKey: filter.include_key,
            excludeKey: filter.exclude_key
        });
        commit('SET_OPEN_FILTER', filter.id);
    },

    /**
     * Remove a filter from the active filters list and clean up its data
     */
    removeFilter({commit, state}, filter) {
        // If this filter is open, close it
        if (state.openFilter === filter.id) {
            commit('SET_OPEN_FILTER', null);
        }

        // Remove from active filters
        commit('REMOVE_ACTIVE_FILTER', filter.id);

        // Clean up data
        commit('REMOVE_FILTER_DATA', {
            includeKey: filter.include_key,
            excludeKey: filter.exclude_key
        });
    },

    /**
     * Toggle a filter (add if not active, remove if active)
     */
    toggleFilter({dispatch, state}, filter) {
        if (state.activeFilters.includes(filter.id)) {
            dispatch('removeFilter', filter);
        } else {
            dispatch('addFilter', filter);
        }
    },

    /**
     * Toggle the accordion for a filter
     */
    toggleAccordion({commit, state}, filter) {
        commit('SET_OPEN_FILTER', state.openFilter === filter.id ? null : filter.id);
    },

    /**
     * Update a filter's value and ensure it's in the active filters list
     */
    updateFiltersValue({commit, state}, {key, value}) {
        commit('UPDATE_FILTER_VALUE', {key, value});

        // Find the relevant filter
        const filter = state.availableFilters.find(
            f => f.include_key === key || f.exclude_key === key
        );

        if (!filter) {
            return;
        }

        // If value is an array, handle each element
        if (Array.isArray(value)) {
            value.forEach(v => {
                commit('ADD_OPTION_TO_FILTER', {
                    filterId: filter.id,
                    option: {name: v, value: v}
                });
            });
        } else {
            commit('ADD_OPTION_TO_FILTER', {
                filterId: filter.id,
                option: {name: value, value}
            });
        }

        // Make sure this filter is in activeFilters
        if (!state.activeFilters.includes(filter.id)) {
            commit('ADD_ACTIVE_FILTER', filter.id);
        }
    },

    /**
     * Set all filter data at once (from external source)
     */
    setFiltersData({commit, dispatch}, data) {
        commit('SET_FILTERS_DATA', data);
        dispatch('syncActiveFilters');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
